import { useState } from "react";
import { Link } from "react-router-dom";
import { PROGRAM_COMPARISON } from "@/data/programComparison";
import { FadeUp } from "./_shared";

export const ProgramComparison = ({ programs }) => {
  const [activeSlug, setActiveSlug] = useState(programs[0].slug);
  const activeProgram = programs.find((program) => program.slug === activeSlug) || programs[0];
  const activeDetails = PROGRAM_COMPARISON[activeProgram.slug];

  return (
    <FadeUp delay={0.12} className="mt-20">
      <div className="border-t-2 border-primary pt-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="iw-overline">Program Atlas</div>
            <h3 data-testid="program-comparison-heading" className="mt-3 font-display text-2xl md:text-3xl font-bold text-primary">
              Find the right starting point.
            </h3>
          </div>
          <p data-testid="program-comparison-pricing-note" className="max-w-md text-sm text-foreground/60 leading-relaxed md:text-right">
            Select a program. Full details live on its dedicated page.
          </p>
        </div>

        <div data-testid="program-comparison-gallery" className="mt-8 border-2 border-primary bg-background">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-px bg-primary/20" role="group" aria-label="Select a program to explore">
            {programs.map((program) => {
              const selected = program.slug === activeSlug;
              return (
                <button
                  key={program.slug}
                  type="button"
                  onPointerDown={() => setActiveSlug(program.slug)}
                  onClick={() => setActiveSlug(program.slug)}
                  aria-pressed={selected}
                  data-testid={`program-comparison-tab-${program.slug}`}
                  className={`group relative aspect-square min-w-0 touch-manipulation overflow-hidden text-left transition-opacity ${selected ? "opacity-100" : "opacity-70 hover:opacity-100"}`}
                >
                  <img
                    src={program.comparisonImage || program.image}
                    alt=""
                    data-testid={`program-comparison-thumbnail-${program.slug}`}
                    className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                  <span className={`absolute inset-0 bg-gradient-to-t from-foreground via-foreground/10 to-transparent ${selected ? "ring-inset ring-4 ring-accent" : ""}`} aria-hidden />
                  <span className="absolute inset-x-0 bottom-0 p-3 font-display text-xs md:text-sm font-bold leading-tight text-white">
                    {program.title}
                  </span>
                </button>
              );
            })}
          </div>

          <div data-testid={`program-comparison-active-${activeSlug}`} className="grid grid-cols-1 lg:grid-cols-12 border-t-2 border-primary">
            <div className="lg:col-span-9 aspect-[5/4] sm:aspect-[4/3] overflow-hidden bg-foreground">
              <img
                src={activeProgram.comparisonImage || activeProgram.image}
                alt={`${activeProgram.title} program visualization`}
                data-testid={`program-comparison-active-image-${activeSlug}`}
                className="h-full w-full object-contain object-center"
                loading="lazy"
              />
            </div>
            <div className="lg:col-span-3 bg-primary text-white p-6 md:p-8 flex flex-col justify-center">
              <div className="font-mono text-xs uppercase tracking-[0.14em] text-white/55">Selected program</div>
              <h4 data-testid="program-comparison-active-title" className="mt-3 font-display text-2xl font-bold leading-tight text-white">
                {activeProgram.title}
              </h4>
              <p data-testid="program-comparison-active-focus" className="mt-4 text-base text-white/75 leading-relaxed">
                {activeDetails.focus}
              </p>
              <Link
                to={`/programs/${activeSlug}`}
                data-testid={`program-comparison-link-${activeSlug}`}
                className="mt-8 inline-flex w-fit border border-white/40 px-5 py-3 font-mono text-xs uppercase tracking-[0.12em] text-white hover:bg-white hover:text-primary transition-colors"
              >
                View Program →
              </Link>
            </div>
          </div>
        </div>

        <p data-testid="program-comparison-consultation-note" className="mt-4 text-center font-mono text-xs uppercase tracking-[0.1em] text-foreground/50">
          Clinical scope, cadence and pricing are confirmed after intake.
        </p>
      </div>
    </FadeUp>
  );
};